import React, { useEffect, useRef } from 'react';
import { SearchResult } from './search-result';
import {
  getSolutionsBaseBySearchQueryInVespa,
  getWixOfferingBySearchQueryInVespa,
} from '../../api/api';
import { biLogger } from '../../bi';
import {
  appMarketPageView,
  searchSearchResults,
} from '@wix/bi-logger-market/v2';
import { config } from '../../config';
import { WithFetcher } from '../../component-decorators/with-fetcher';
import { SearchResultEmptyStateFetcher } from './search-result-empty-state';
import { CenteredLoader } from '../centered-loader';
import { isBrowser } from '../../common/utils';
import { Origin } from '../../enums/marketplace-enums';
import { SearchResultsSEO } from './search-result-seo';
import { useConfigContext } from '../../config-context';
import { useMarketplaceStore } from '../../component-decorators/with-marketplace-store';
import {
  getPathForBi,
  getReferralInfo,
  getWidgetReferral,
} from '../../common/bi-events';
import { AppMarketPage, SuggestionType } from '../../types/common/marketplace';
import { composer } from '../../component-decorators/composer';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';

export const SearchResultFetcher = composer()
  .withTranslation()
  .withExperiments()
  .compose((props) => {
    const { locale, isMobile, rpcClient } = useConfigContext();

    return (
      <WithFetcher
        loader={<CenteredLoader />}
        fetchMemoizationId={`search-result-${props.query}`}
        initialFetch={() =>
          getSearchResultData(
            props.query,
            locale,
            props.experiments,
            36,
            0,
            rpcClient,
          )
        }
      >
        {({ fetchData, reFetch }) => (
          <>
            {config.origin === Origin.STANDALONE && <SearchResultsSEO />}
            <SearchResultContainer
              fetchData={fetchData}
              reFetch={reFetch}
              locale={locale}
              isMobile={isMobile}
              query={props.query}
              experiments={props.experiments}
              t={props.t}
            />
          </>
        )}
      </WithFetcher>
    );
  });

function SearchResultContainer({
  fetchData,
  reFetch,
  locale,
  query,
  isMobile,
  t,
  experiments,
}) {
  const didComponentMountRef = useRef(false);
  const marketplaceStore = useMarketplaceStore();
  function isWixOfferingApp(app) {
    return (
      (app.type === 'APP' && app.showRegularAppPage) ||
      app.type === 'WIX_OFFERING'
    );
  }
  const suggestedApps =
    config.origin === Origin.EDITOR &&
    (config.editorType === 'EDITORX' || config.editorType === 'STUDIO')
      ? fetchData.suggestions.filter(
          // filter out wix offerings in editorx's editor since the onClick of the box is not working there
          (suggestion) => !isWixOfferingApp(suggestion),
        )
      : fetchData.suggestions;

  useEffect(() => {
    if (didComponentMountRef.current) {
      reFetch(() => getSearchResultData(query, locale, experiments, 36, 0));
    } else {
      didComponentMountRef.current = true;
    }
  }, [query, locale]);

  useEffect(() => {
    biLogger.report(
      appMarketPageView({
        market: config.originForBI,
        tag_name: query,
        tag_type: AppMarketPage.SEARCH_RESULT,
        navigation_source: marketplaceStore?.route?.searchLocation,
        referral_info: getReferralInfo(
          marketplaceStore.route && marketplaceStore.route.referral,
          marketplaceStore.prevRoute,
        ),
        search_term: query,
        widget_referral: getWidgetReferral(),
        utm_id: marketplaceStore?.route?.utm_id,
        referral_tag_name: marketplaceStore?.prevRoute?.slug?.replace(
          /-/g,
          '_',
        ),
        referral_tag_type: getPathForBi(marketplaceStore?.prevRoute?.path),
        referral_sub_tag_name:
          marketplaceStore?.prevRoute?.subCategories?.join(','),
        is_external:
          marketplaceStore?.route?.referral &&
          marketplaceStore?.route?.referral !== 'manage_apps',
      }),
    );

    biLogger.report(
      searchSearchResults({
        first_result_app_id:
          suggestedApps.length > 0 ? suggestedApps[0].id : null,
        market: config.originForBI,
        num_results: suggestedApps.length,
        search_term: query,
      }),
    );
  }, [fetchData]);

  useEffect(() => {
    if (isBrowser()) {
      window.scrollTo(0, 0);
    }
  });

  if (!query || suggestedApps.length === 0) {
    return <SearchResultEmptyStateFetcher query={query} t={t} />;
  }

  return (
    <SearchResult
      query={query}
      locale={locale}
      isMobile={isMobile}
      numOfResults={suggestedApps.length}
      searchResultHeaderProps={{
        query,
        isResult: true,
        numOfResults: suggestedApps.length,
      }}
      solutionGridWrapperProps={{
        t,
        isCompany: true,
        solutions: suggestedApps.map((solution, appIndex) => ({
          appIndex,
          isPreInstalled: solution?.showRegularAppPage,
          ...solution,
        })),
      }}
    />
  );
}

// ------------------------------HELPER-FUNCTIONS------------------------------

async function getSearchResultData(
  query: string,
  locale: string,
  experiments: any,
  limit: number,
  apiOffset: number = 0,
  rpcClient?: AppMarketRpcClient,
) {
  const [searchResult, wixOfferingResult] = await Promise.all([
    getSolutionsBaseBySearchQueryInVespa(
      query,
      locale,
      limit,
      apiOffset,
      rpcClient,
    ),
    getWixOfferingBySearchQueryInVespa(
      query,
      locale,
      limit,
      apiOffset,
      rpcClient,
    ),
  ]);

  const apps =
    searchResult?.suggestions?.map((app) => ({
      ...app,
      suggestionType: SuggestionType.APP,
    })) || [];
  const wixOfferingSuggestions =
    wixOfferingResult?.suggestions?.map((app) => ({
      ...app,
      suggestionType: SuggestionType.WIX_OFFERING,
    })) || [];

  for (let i = 1; i <= wixOfferingSuggestions.length; i++) {
    const position = apps.length < 5 ? apps.length + i : i * 3 + 1;
    apps.splice(position, 0, wixOfferingSuggestions[i - 1]);
  }

  return { suggestions: apps, total: apps.length };
}
